import React from 'react';

interface AuthErrorMessageProps {
  error: string | null;
  errorCode?: string;
  onClose?: () => void;
}

/**
 * Composant d'affichage des erreurs d'authentification avec suggestions de résolution
 */
const AuthErrorMessage: React.FC<AuthErrorMessageProps> = ({ 
  error, 
  errorCode,
  onClose 
}) => {
  if (!error) return null;

  // Fonction pour obtenir une suggestion basée sur le code d'erreur ou le message
  const getSuggestion = (): string => {
    if (errorCode) {
      switch (errorCode) {
        case 'auth/user-not-found':
          return 'Vérifiez que votre adresse email est correcte ou créez un nouveau compte.';
        case 'auth/wrong-password':
          return 'Vérifiez votre mot de passe ou utilisez l\'option "Mot de passe oublié".';
        case 'auth/email-already-in-use':
          return 'Essayez de vous connecter plutôt que de créer un compte.';
        case 'auth/invalid-email':
          return 'Vérifiez le format de votre adresse email.';
        case 'auth/weak-password':
          return 'Choisissez un mot de passe plus fort avec au moins 6 caractères.';
        case 'auth/network-request-failed':
          return 'Vérifiez votre connexion internet et réessayez.';
        case 'auth/too-many-requests':
          return 'Trop de tentatives. Attendez quelques minutes avant de réessayer.';
        case 'auth/account-exists-with-different-credential':
          return 'Un compte existe déjà avec cette adresse email. Essayez une autre méthode de connexion.';
        case 'auth/popup-closed-by-user':
          return 'Vous avez fermé la fenêtre de connexion. Veuillez réessayer.';
        case 'api_error':
          return 'Le serveur rencontre un problème. Veuillez réessayer plus tard.';
        default:
          break;
      }
    }

    // Si aucun code d'erreur ou cas non géré, suggestions basées sur le message
    if (error.toLowerCase().includes('mot de passe')) {
      return 'Vérifiez votre mot de passe ou utilisez la fonctionnalité "Mot de passe oublié".';
    } else if (error.toLowerCase().includes('email') || error.toLowerCase().includes('utilisateur')) {
      return 'Vérifiez que votre adresse email est correcte ou créez un nouveau compte.';
    } else if (error.toLowerCase().includes('réseau') || error.toLowerCase().includes('connexion')) {
      return 'Vérifiez votre connexion internet et réessayez.';
    }

    // Message par défaut
    return 'Si le problème persiste, contactez le support.';
  };

  return (
    <div className="auth-error-message">
      <div className="auth-error-content">
        <div className="auth-error-icon">⚠️</div>
        <div className="auth-error-text">
          <p className="auth-error-title">{error}</p>
          <p className="auth-error-suggestion">{getSuggestion()}</p>
        </div>
        {onClose && (
          <button 
            className="auth-error-close" 
            onClick={onClose}
            aria-label="Fermer"
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
};

export default AuthErrorMessage;
